import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const { updateSettings } = useSettings();
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const token = localStorage.getItem('admin_token');

  const defaultSettings = {
    id: 'main',
    site_title: 'WatsFlow',
    logo_url: '',
    logo_text: 'WatsFlow',
    hero_title_ar: 'ارسل رسائل واتساب بكل سهولة',
    hero_title_en: 'Send WhatsApp Messages Easily',
    hero_subtitle_ar: 'وفر وقتك وجهدك مع خدمة API واتساب الاحترافية',
    hero_subtitle_en: 'Save time and effort with our professional WhatsApp API service',
    contact_email: 'info@watsflow.com',
    contact_phone: '+966500000000',
    whatsapp_number: '+966500000000',
    client_area_url: '',
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings({ ...defaultSettings, ...(response.data || {}) });
      } catch (error) {
        console.error('Error fetching settings:', error);
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = { ...defaultSettings, ...settings };
      await axios.put(`${API}/admin/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateSettings(payload);
      toast.success(language === 'ar' ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(language === 'ar' ? 'خطأ في حفظ الإعدادات' : 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('admin_settings')}</h1>
        <p className="text-slate-500">
          {language === 'ar' ? 'إعدادات الموقع العامة' : 'General website settings'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Settings */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'ar' ? 'إعدادات الشعار' : 'Logo Settings'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {language === 'ar' ? 'رابط الشعار (صورة)' : 'Logo URL (image)'}
              </label>
              <Input
                value={settings.logo_url || ''}
                onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png or /logo.png"
                dir="ltr"
                data-testid="logo-url"
              />
              <p className="text-sm text-slate-500 mt-1">
                {language === 'ar'
                  ? 'اتركه فارغاً لاستخدام الشعار الافتراضي. أدخل رابط صورة أو مسار مثل /logo.png'
                  : 'Leave empty for default logo. Enter image URL or path like /logo.png'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {language === 'ar' ? 'نص الشعار' : 'Logo text'}
              </label>
              <Input
                value={settings.logo_text || ''}
                onChange={(e) => setSettings({ ...settings, logo_text: e.target.value })}
                placeholder="WatsFlow"
                data-testid="logo-text"
              />
              <p className="text-sm text-slate-500 mt-1">
                {language === 'ar' ? 'النص الظاهر بجانب الشعار' : 'Text displayed next to the logo'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Site Title (Browser Tab) */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'ar' ? 'عنوان الموقع (تبويب المتصفح)' : 'Site Title (Browser Tab)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {language === 'ar' ? 'العنوان الظاهر في تبويب المتصفح' : 'Title shown in browser tab'}
              </label>
              <Input
                value={settings.site_title || ''}
                onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
                placeholder="WatsFlow"
                data-testid="site-title"
              />
            </div>
          </CardContent>
        </Card>

        {/* Hero Section Settings */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'ar' ? 'إعدادات القسم الرئيسي' : 'Hero Section Settings'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_hero_title_ar')}
                </label>
                <Input
                  value={settings.hero_title_ar}
                  onChange={(e) => setSettings({ ...settings, hero_title_ar: e.target.value })}
                  data-testid="hero-title-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_hero_title_en')}
                </label>
                <Input
                  value={settings.hero_title_en}
                  onChange={(e) => setSettings({ ...settings, hero_title_en: e.target.value })}
                  data-testid="hero-title-en"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_hero_subtitle_ar')}
                </label>
                <Textarea
                  value={settings.hero_subtitle_ar}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle_ar: e.target.value })}
                  rows={3}
                  data-testid="hero-subtitle-ar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('admin_hero_subtitle_en')}
                </label>
                <Textarea
                  value={settings.hero_subtitle_en}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle_en: e.target.value })}
                  rows={3}
                  data-testid="hero-subtitle-en"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Settings */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'ar' ? 'معلومات التواصل' : 'Contact Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('contact_email')}
                </label>
                <Input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                  data-testid="contact-email-setting"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('contact_phone')}
                </label>
                <Input
                  value={settings.contact_phone}
                  onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                  dir="ltr"
                  data-testid="contact-phone-setting"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('contact_whatsapp')}
                </label>
                <Input
                  value={settings.whatsapp_number}
                  onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                  dir="ltr"
                  data-testid="whatsapp-setting"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Client Area Integration */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'ar' ? 'تكامل Client Area' : 'Client Area Integration'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('admin_client_area_url')}
              </label>
              <Input
                value={settings.client_area_url}
                onChange={(e) => setSettings({ ...settings, client_area_url: e.target.value })}
                placeholder="https://clients.yoursite.com"
                dir="ltr"
                data-testid="client-area-url"
              />
              <p className="text-sm text-slate-500 mt-1">
                {language === 'ar'
                  ? 'رابط نظام Client Area الخاص بك (PHP/MySQL)'
                  : 'URL to your Client Area system (PHP/MySQL)'}
              </p>
            </div>

            {/* API Documentation */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl">
              <h4 className="font-medium text-slate-900 mb-3">
                {language === 'ar' ? 'توثيق API للربط مع Client Area' : 'API Documentation for Client Area Integration'}
              </h4>
              <div className="bg-slate-900 rounded-xl p-4 space-y-3 font-mono text-sm overflow-x-auto">
                <div>
                  <p className="text-emerald-400">GET /api/client-area/packages</p>
                  <p className="text-slate-400 text-xs mt-1">
                    {language === 'ar' ? 'جلب جميع الباقات المتاحة' : 'Fetch all available packages'}
                  </p>
                </div>
                <div>
                  <p className="text-emerald-400">GET /api/client-area/package/:id</p>
                  <p className="text-slate-400 text-xs mt-1">
                    {language === 'ar' ? 'جلب تفاصيل باقة محددة' : 'Fetch specific package details'}
                  </p>
                </div>
                <div>
                  <p className="text-emerald-400">POST /api/client-area/webhook</p>
                  <p className="text-slate-400 text-xs mt-1">
                    {language === 'ar' ? 'استقبال أحداث من Client Area' : 'Receive events from Client Area'}
                  </p>
                  <p className="text-slate-500 text-xs mt-2">
                    Body: {'{'} event: string, client_id: string, package_id?: string, data?: object {'}'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            className="btn-primary flex items-center gap-2"
            disabled={saving}
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {t('admin_save')}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
